import { useEffect, useMemo, useState } from 'react';
import { FiChevronRight, FiCreditCard, FiExternalLink } from 'react-icons/fi';

import { refreshAccessTokenNow } from '@/api/api';
import { imageVariantLink } from '@/common/media';
import { Avatar, Header, LoaderSpinner } from '@/components';
import { useNoYachtFallback } from '@/hooks/useNoYachtFallback';
import { usePerson } from '@/store/persons/person.store';
import { getPrimaryRole, normalizeRoles, UserRole } from '@/utils/roles';

import { ReferralCard } from './ReferralCard';
import { planSummary, soonestTrialEnd, statusView } from './subscription-display';
import s from './SubscriptionSettingsPage.module.scss';
import { useManagedVesselSubscriptions } from './useManagedVesselSubscriptions';
import { VesselDetail } from './VesselDetail';

// External subscription/billing portal. Domain is env-driven; the app's access
// token is forwarded as a query param so the portal can authenticate the session.
const SUBSCRIPTION_PORTAL_URL =
  import.meta.env.VITE_SUBSCRIPTION_PORTAL_URL ?? 'http://localhost:5173';

const openSubscriptionPortal = async () => {
  const accessToken = await refreshAccessTokenNow();
  const url = new URL(SUBSCRIPTION_PORTAL_URL);
  url.searchParams.set('access_token', accessToken);
  window.location.href = url.toString();
};

const SubscriptionContent = () => {
  const NoYachtFallback = useNoYachtFallback();
  const { items, isLoading, isSubscriptionsPending, error } = useManagedVesselSubscriptions();
  const { currentUser } = usePerson();
  const isGuest =
    getPrimaryRole(normalizeRoles(currentUser?.services?.map((s) => s.role))) === UserRole.GUEST;
  const [openingPortal, setOpeningPortal] = useState(false);
  // null = the vessel list. The open vessel is looked up by id so it follows a refetch.
  const [openVesselId, setOpenVesselId] = useState<string | null>(null);

  useEffect(() => {
    const onPageShow = () => setOpeningPortal(false);
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // The referral card names the trial that can be extended — the soonest one to run out.
  const trialEndsAt = useMemo(
    () => soonestTrialEnd(items.map(({ subscription }) => subscription)),
    [items],
  );

  const handleOpenPortal = async () => {
    if (openingPortal) return;
    setOpeningPortal(true);
    try {
      await openSubscriptionPortal();
    } catch {
      setOpeningPortal(false);
    }
  };

  // ---- Renderers ----------------------------------------------------------

  const portalButton = (
    <button
      type="button"
      className={s.portalBtn}
      onClick={handleOpenPortal}
      disabled={openingPortal}
    >
      <FiExternalLink />
      <span>{openingPortal ? 'Preparing…' : 'Manage Account Online'}</span>
    </button>
  );

  const renderVesselRows = () => {
    if (isLoading) {
      return (
        <div className={s.listState}>
          <LoaderSpinner fullHeight={false} />
        </div>
      );
    }
    if (error) {
      return <div className={s.empty}>Couldn’t load your vessels. Please try again.</div>;
    }
    if (items.length === 0) {
      return <div className={s.empty}>Please add a vessel to get started.</div>;
    }
    return items.map(({ vessel, subscription }) => {
      const status = statusView(subscription);
      return (
        <button
          key={vessel.id}
          type="button"
          className={`${s.row} ${s.roleRow}`}
          onClick={() => setOpenVesselId(vessel.id)}
        >
          <Avatar
            src={imageVariantLink(vessel.image, 38) || NoYachtFallback}
            firstName={vessel.vessel_name}
            size={38}
          />
          <div className={s.rowText}>
            <div className={s.rowTitle}>{vessel.vessel_name}</div>
            <div className={s.rowSub}>
              {isSubscriptionsPending ? 'Loading plan…' : planSummary(subscription)}
            </div>
          </div>
          {!isSubscriptionsPending && (
            <span className={`${s.statusChip} ${s[`status_${status.tone}`]}`}>{status.label}</span>
          )}
          <FiChevronRight className={s.navChevron} />
        </button>
      );
    });
  };

  const renderVesselList = () => (
    <>
      <header className={`${s.detailHeader} ${s.detailHeaderMain}`}>
        <div className={s.detailIcon}>
          <FiCreditCard />
        </div>
        <div className={s.detailHeaderText}>
          <h2 className={s.detailTitle}>Manage My Account</h2>
          <p className={s.detailSub}>Plans and crew seats across your vessels.</p>
        </div>
        {!isGuest && portalButton}
      </header>

      {!isGuest && (
        <>
          <p className={s.groupLabel}>Your vessels{items.length > 0 && ` · ${items.length}`}</p>
          <div className={s.group}>{renderVesselRows()}</div>
        </>
      )}

      <ReferralCard trialEndsAt={trialEndsAt} />
    </>
  );

  const openItem = items.find(({ vessel }) => vessel.id === openVesselId);

  return (
    <div className={s.page} key={openVesselId ?? 'list'}>
      {openItem ? (
        <VesselDetail item={openItem} onBack={() => setOpenVesselId(null)} />
      ) : (
        renderVesselList()
      )}
    </div>
  );
};

export default function SubscriptionSettingsPage({
  embedded = false,
}: { embedded?: boolean } = {}) {
  return (
    <>
      {!embedded && <Header title="Manage My Account" backButton />}
      <SubscriptionContent />
    </>
  );
}
